import { ChangeEvent, forwardRef, KeyboardEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { WebsiteModalTypeEnum } from '@/Domain/enum/WebsiteModalType.enum';
import { AppHelper } from '@/Domain/helper/App.helper';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';

type Props = {
  type: WebsiteModalTypeEnum;
  value: string;
  onChange: (value: string) => void;
  onError?: (error: string | null) => void;
  reset?: boolean;
  onResetComplete?: () => void;
  error?: boolean;
  disabled?: boolean;
  validate?: (url: string) => string | null;
  inputId?: string;
  describedBy?: string;
  /**
   * Forwarded to the inner `Input` / `AutosizeTextarea` so callers can
   * override surface styling from the outside without patching the
   * shared component's defaults.
   */
  className?: string;
};

export const UrlInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, Props>(
  (
    {
      type,
      value,
      onChange,
      onError,
      reset,
      onResetComplete,
      error,
      disabled,
      validate,
      inputId,
      describedBy,
      className,
    },
    ref,
  ) => {
    const { t } = useTranslation(['knowledge_base']);
    // Lazy-initialize from the `value` prop so the input shows the parent's
    // current URL on the very first render. The previous mount-effect-only
    // approach left `displayValue` empty for one render after mount — visible
    // when the parent lifts the URL across component unmounts (e.g.
    // GettingStartedPage's `urlDraft` surviving an automatic↔manual toggle):
    // the empty render briefly clobbered the user's typed value before the
    // effect kicked in.
    const [displayValue, setDisplayValue] = useState(() =>
      value ? AppHelper.stripProtocol(value) : '',
    );

    useEffect(() => {
      if (reset) {
        onChange('');
        setDisplayValue('');
        if (onResetComplete) {
          onResetComplete();
        }
      }
    }, [reset, onChange, onResetComplete]);

    const validateUrl = (url: string): string | null => {
      if (!url.trim()) {
        return t('modals.upload_url.errors.url_required');
      }

      if (type === WebsiteModalTypeEnum.MULTIPLE) {
        const nonEmptyUrls = UploadUrlModalHelper.parseMultipleUrls(url);

        if (nonEmptyUrls.length === 0) {
          return t('modals.upload_url.errors.urls_required');
        }

        const allValid = nonEmptyUrls.every((u) =>
          UploadUrlModalHelper.validateUrl(AppHelper.addProtocol(u)),
        );
        if (!allValid) {
          return t('modals.upload_url.errors.contains_invalid_url');
        }

        return null;
      }

      if (!UploadUrlModalHelper.validateUrl(AppHelper.addProtocol(url))) {
        return t('modals.upload_url.errors.invalid_url');
      }

      return null;
    };

    const handleUrlChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      if (type !== WebsiteModalTypeEnum.MULTIPLE) {
        // Pure pass-through: don't reach into the typed value beyond the
        // `https://` round-trip. Earlier on this PR we tried to silently
        // trim/clip whitespace here, but it caused two paper-cuts:
        //   1. Pressing space at the start of an existing value left the
        //      DOM and React state out of sync; the cursor visibly jumped
        //      to the end of the field.
        //   2. The user couldn't type a leading space at all, even briefly.
        // Whitespace is still rejected at submit time by the form's
        // `UploadUrlModalHelper.validateUrl` check (red border), so the
        // bad URL never reaches the backend.
        const strippedValue = AppHelper.stripProtocol(newValue);
        setDisplayValue(strippedValue);

        onChange(AppHelper.addProtocol(strippedValue));
      } else {
        onChange(newValue);
        setDisplayValue(newValue);
      }

      if (onError) {
        onError(null);
      }
    };

    const handleUrlBlur = () => {
      const validationError = (validate ?? validateUrl)(value);
      if (onError) {
        onError(validationError);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (type === WebsiteModalTypeEnum.MULTIPLE && e.key === ' ') {
        e.preventDefault();

        const cursorPosition = e.currentTarget.selectionStart || 0;
        const beforeCursor = displayValue.substring(0, cursorPosition);
        const afterCursor = displayValue.substring(cursorPosition);

        const newDisplayValue = beforeCursor + '\n' + afterCursor;
        setDisplayValue(newDisplayValue);
        onChange(newDisplayValue);

        const textareaElement = e.currentTarget;

        requestAnimationFrame(() => {
          if (textareaElement && document.body.contains(textareaElement)) {
            try {
              const newPosition = cursorPosition + 1;
              textareaElement.focus();
              textareaElement.setSelectionRange(newPosition, newPosition);
            } catch (error) {
              console.error('Error setting selection range:', error);
            }
          }
        });
      }
    };

    return (
      <>
        {type === WebsiteModalTypeEnum.MULTIPLE ? (
          // Explicit `maxHeight` opts out of the parent-clientHeight clamp in
          // `AutosizeTextarea` — otherwise the autosize effect measures the
          // parent's current height (which is just the textarea itself) and
          // pins the cap to the initial size, so pasted URLs past line 2
          // scroll instead of growing the field. `minHeight`/`maxHeight` give
          // the user a comfortable 5-line window that expands to 10 before
          // scrolling kicks in. `leading-6` matches the 20px px-y line rhythm
          // with the inner padding so wrapped URLs don't look cramped.
          <AutosizeTextarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            placeholder={t('modals.upload_url.multiple_pages.url_placeholder')}
            value={displayValue}
            onChange={handleUrlChange}
            onBlur={handleUrlBlur}
            onKeyDown={handleKeyDown}
            id={inputId}
            aria-invalid={error || undefined}
            aria-describedby={describedBy}
            error={error}
            minHeight={120}
            maxHeight={240}
            className={`py-3 leading-6 ${className ?? ''}`}
            disabled={disabled}
          />
        ) : (
          // Use the shared `Input`'s `startAdornment` slot for the `https://`
          // prefix so the component inherits the app-wide focus ring, error
          // border, and disabled styling — no custom wrapper that would
          // otherwise disable those behaviors.
          <Input
            ref={ref as React.Ref<HTMLInputElement>}
            placeholder={
              type === WebsiteModalTypeEnum.SITEMAP
                ? t('modals.upload_url.sitemap.url_placeholder')
                : t('modals.upload_url.single_page.url_placeholder')
            }
            value={displayValue}
            onChange={handleUrlChange}
            onBlur={handleUrlBlur}
            id={inputId}
            aria-invalid={error || undefined}
            aria-describedby={describedBy}
            startAdornment="https://"
            error={error}
            disabled={disabled}
            className={className}
          />
        )}
      </>
    );
  },
);

UrlInput.displayName = 'UrlInput';
